'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { apiFetch } from '@/lib/api-fetch'
import {
  FISHING_DAY_PHASE_LABELS,
  FishingDayPhase,
  FishingNaturePeriod,
  fishingSolarStateFromSchedule,
  fishingDayPhaseAtHour,
  type FishingSolarSchedule,
} from '@/lib/fishing-day-phase'
import { FishingEventType, type FishingSaleEvent } from '@/lib/fishing-events'
import { fishingCatchTier } from '@/lib/fishing-catch'
import { createFishingWorldRenderDecision } from '@/lib/fishing-world-render-contract'
import { FishingWorldAmbientAdmissionRejection, type FishingWorldAmbientAdmissionRejection as FishingWorldAmbientAdmissionRejectionType } from '@/lib/fishing-world-render-contract'
import { FISHING_WORLD_EVENT_LABELS, fishingWorldDebugSimulations, isFishingWorldDebugEnabled } from '@/lib/fishing-world-debug'
import { createFishingWorldAudioPlaybackHistory, createFishingWorldEventAudio } from '@/lib/fishing-world-audio'
import { FishingWorldSchedulerSuppressionReason, type FishingWorldSchedulerSuppressionReason as FishingWorldSchedulerSuppressionReasonType } from '@/lib/fishing-world-scheduler'
import {
  FishingRadioProvider,
  RADIO_101_CHANNEL_URL,
  RADIO_101_LOFI_STREAM_MIME,
  RADIO_101_LOFI_STREAM_URL,
  RADIO_101_UNSUPPORTED_HINT,
  RADIO_101_UNSUPPORTED_LINK_LABEL,
  RADIO_101_UNSUPPORTED_MESSAGE,
} from '@/lib/fishing-radio'
import { FishingScene } from './FishingScene'
import type { BadgerSide } from './AmbientWildlife'
import { useFishingWorldRuntime } from './useFishingWorldRuntime'
import { useFishingCatchSound } from './useFishingCatchSound'
import { useFishingNatureSound } from './useFishingNatureSound'
import styles from './FishingExperience.module.css'

const RADIO_EMBED_URL = 'https://loficafe.net/embed/working?autoplay=1'
const POLL_INTERVAL_MS = 10_000
const CATCH_DURATION_MS = 4_800

const SCHEDULER_SUPPRESSION_LABELS: Readonly<Record<FishingWorldSchedulerSuppressionReasonType, string>> = {
  [FishingWorldSchedulerSuppressionReason.InvalidCatalogPolicy]: 'некорректная политика каталога',
  [FishingWorldSchedulerSuppressionReason.DocumentHidden]: 'вкладка скрыта',
  [FishingWorldSchedulerSuppressionReason.ReducedMotion]: 'уменьшение движения',
  [FishingWorldSchedulerSuppressionReason.DeferredRescheduled]: 'расписание перенесено',
  [FishingWorldSchedulerSuppressionReason.MajorCadence]: 'ожидание большого события',
  [FishingWorldSchedulerSuppressionReason.MajorActive]: 'активно большое событие',
  [FishingWorldSchedulerSuppressionReason.EventCadence]: 'ожидание каденции',
  [FishingWorldSchedulerSuppressionReason.EventCooldown]: 'перерыв события',
  [FishingWorldSchedulerSuppressionReason.EventAlreadyActive]: 'событие уже активно',
  [FishingWorldSchedulerSuppressionReason.ExclusiveCollision]: 'конфликт эксклюзивности',
  [FishingWorldSchedulerSuppressionReason.MinorCompositionLimit]: 'лимит фоновых событий',
  [FishingWorldSchedulerSuppressionReason.MinorVisualBudget]: 'лимит визуального бюджета',
  [FishingWorldSchedulerSuppressionReason.QuietWindow]: 'тихое окно',
  [FishingWorldSchedulerSuppressionReason.NoEnabledEvents]: 'нет доступных событий',
  [FishingWorldSchedulerSuppressionReason.InvalidPlacement]: 'некорректное размещение события',
}

const DEBUG_REJECTION_LABELS: Readonly<Record<FishingWorldAmbientAdmissionRejectionType | 'invalid-placement' | 'lifecycle-suppressed', string>> = {
  [FishingWorldAmbientAdmissionRejection.EventAlreadyActive]: 'событие этого вида уже активно',
  [FishingWorldAmbientAdmissionRejection.MajorActive]: 'уже активно большое событие',
  [FishingWorldAmbientAdmissionRejection.MinorCompositionLimit]: 'достигнут лимит фоновых событий',
  [FishingWorldAmbientAdmissionRejection.MinorVisualBudget]: 'достигнут визуальный бюджет',
  'invalid-placement': 'привязка не прошла проверку',
  'lifecycle-suppressed': 'событие недоступно при текущем состоянии сцены',
}

function formatMoney(amount: number) {
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(amount)} ₽`
}

function formatTime(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? ''
    : new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(date)
}

function formatNextEligibility(atMs: number, nowMs = Date.now()) {
  if (!Number.isFinite(atMs)) return 'не определено'
  const remainingMs = atMs - nowMs
  if (remainingMs <= 0) return 'доступно сейчас'
  const remainingMinutes = Math.ceil(remainingMs / 60_000)
  if (remainingMinutes < 60) return `через ${remainingMinutes} мин.`
  const hours = Math.floor(remainingMinutes / 60)
  const minutes = remainingMinutes % 60
  return minutes === 0 ? `через ${hours} ч` : `через ${hours} ч ${minutes} мин.`
}

function dateKeyInTimeZone(value: string | Date, timeZone: string) {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date)
}

function activeCatches(events: readonly FishingSaleEvent[], timeZone: string, today: string | null) {
  const uniqueEvents = Array.from(new Map(events.map((event) => [event.id, event])).values())
    .sort((left, right) => Date.parse(left.occurredAt) - Date.parse(right.occurredAt))
  const catches = new Map<string, FishingSaleEvent>()
  for (const event of uniqueEvents) {
    if (event.type === FishingEventType.Release) catches.delete(event.catchId)
    else if (dateKeyInTimeZone(event.catchOccurredAt, timeZone) === today) catches.set(event.catchId, event)
  }
  return Array.from(catches.values())
    .sort((left, right) => Date.parse(right.catchOccurredAt) - Date.parse(left.catchOccurredAt))
    .slice(0, 5)
}

export default function FishingExperience() {
  const rootRef = useRef<HTMLDivElement>(null)
  const seenEvents = useRef(new Set<string>())
  const initialized = useRef(false)
  const pollInFlight = useRef(false)
  const refreshQueued = useRef(false)
  const mountedRef = useRef(false)
  const pollRef = useRef<(followUp?: boolean) => Promise<void>>(() => Promise.resolve())
  const solarScheduleExpiresAt = useRef(0)
  const [queue, setQueue] = useState<FishingSaleEvent[]>([])
  const [activeEvent, setActiveEvent] = useState<FishingSaleEvent | null>(null)
  const [recentEvents, setRecentEvents] = useState<FishingSaleEvent[]>([])
  const [todayCatchCount, setTodayCatchCount] = useState(0)
  const [todayCatchAmount, setTodayCatchAmount] = useState(0)
  const [connected, setConnected] = useState(false)
  const [radioStarted, setRadioStarted] = useState(true)
  const [radioProvider, setRadioProvider] = useState(FishingRadioProvider.LofiCafe)
  const [radioUnsupported, setRadioUnsupported] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [localHour, setLocalHour] = useState(12)
  const [solarDayPhase, setSolarDayPhase] = useState<FishingDayPhase>(FishingDayPhase.Day)
  const [naturePeriod, setNaturePeriod] = useState<FishingNaturePeriod>(FishingNaturePeriod.Daylight)
  const [solarSchedule, setSolarSchedule] = useState<FishingSolarSchedule | null>(null)
  const [debugHour, setDebugHour] = useState<number | null>(null)
  const [debugCatchFrame, setDebugCatchFrame] = useState<number | null>(null)
  const [visibleBadgerSide, setVisibleBadgerSide] = useState<BadgerSide | null>(null)
  const [mounted, setMounted] = useState(false)

  const catchSound = useFishingCatchSound(activeEvent)
  const { activeEvents, completeAmbientEvent, simulateAmbientEvent, diagnostics } = useFishingWorldRuntime()
  const worldDecision = createFishingWorldRenderDecision(activeEvents)
  const reducedMotionRef = useRef(diagnostics.reducedMotion)
  reducedMotionRef.current = diagnostics.reducedMotion
  const worldAudio = useRef(createFishingWorldEventAudio({ reducedMotion: () => reducedMotionRef.current }))
  const playedWorldAudioEvents = useRef(createFishingWorldAudioPlaybackHistory())

  useEffect(() => {
    mountedRef.current = true
    setMounted(true)
    return () => {
      mountedRef.current = false
      refreshQueued.current = false
    }
  }, [])

  useEffect(() => {
    for (const event of activeEvents) {
      if (playedWorldAudioEvents.current.claim(event.id)) worldAudio.current.play(event.kind)
    }
  }, [activeEvents])

  useEffect(() => {
    const updateClock = () => setLocalHour(new Date().getHours())
    updateClock()
    const timer = window.setInterval(updateClock, 60_000)
    return () => window.clearInterval(timer)
  }, [])

  const poll = useCallback(async (followUp = false) => {
    if (document.visibilityState === 'hidden') return
    if (pollInFlight.current) {
      if (followUp) refreshQueued.current = true
      return
    }
    refreshQueued.current = false
    pollInFlight.current = true
    try {
      const includeToday = !initialized.current || Date.now() >= solarScheduleExpiresAt.current
      const viewerTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
      const response = await apiFetch(
        `/api/fishing/events?limit=40&timeZone=${encodeURIComponent(viewerTimeZone)}${includeToday ? '&includeToday=1' : ''}`,
        { cache: 'no-store' },
      )
      if (!response.ok) throw new Error('feed unavailable')
      const payload = await response.json() as {
        events?: FishingSaleEvent[]
        todayCount?: number
        todayAmount?: number
        feedEventIds?: string[]
        solarSchedule?: FishingSolarSchedule
        radioProvider?: FishingRadioProvider
      }
      if (!mountedRef.current) return
      const events = Array.isArray(payload.events) ? payload.events : []
      const today = dateKeyInTimeZone(new Date(), viewerTimeZone)
      if (payload.solarSchedule) {
        const solarState = fishingSolarStateFromSchedule(payload.solarSchedule, new Date())
        setSolarSchedule(payload.solarSchedule)
        setSolarDayPhase(solarState.dayPhase)
        setNaturePeriod(solarState.naturePeriod)
        solarScheduleExpiresAt.current = Date.parse(payload.solarSchedule.validUntil)
      }
      if (Object.values(FishingRadioProvider).includes(payload.radioProvider as FishingRadioProvider)) {
        setRadioProvider(payload.radioProvider as FishingRadioProvider)
      }
      if (Number.isFinite(payload.todayCount)) {
        setTodayCatchCount(Math.max(0, payload.todayCount || 0))
      }
      if (Number.isFinite(payload.todayAmount)) {
        setTodayCatchAmount(Math.max(0, payload.todayAmount || 0))
      }
      if (!initialized.current) {
        setRecentEvents(activeCatches(events, viewerTimeZone, today))
        for (const eventId of payload.feedEventIds || []) seenEvents.current.add(eventId)
        for (const event of events) seenEvents.current.add(event.id)
        initialized.current = true
      } else {
        const fresh = events.filter((event) => !seenEvents.current.has(event.id)).reverse()
        for (const event of fresh) seenEvents.current.add(event.id)
        if (fresh.length > 0) {
          setRecentEvents((current) => activeCatches([...fresh, ...current], viewerTimeZone, today))
          setQueue((current) => [...current, ...fresh])
          if (!Number.isFinite(payload.todayCount)) {
            const delta = fresh.reduce((sum, event) => {
              if (dateKeyInTimeZone(event.catchOccurredAt, viewerTimeZone) !== today) return sum
              return sum + (event.type === FishingEventType.Release ? -1 : 1)
            }, 0)
            setTodayCatchCount((count) => Math.max(0, count + delta))
          }
          if (!Number.isFinite(payload.todayAmount)) {
            const amountDelta = fresh.reduce((sum, event) => {
              if (dateKeyInTimeZone(event.catchOccurredAt, viewerTimeZone) !== today) return sum
              return sum + (event.type === FishingEventType.Release ? -event.amount : event.amount)
            }, 0)
            setTodayCatchAmount((amount) => Math.max(0, amount + amountDelta))
          }
        }
      }
      setConnected(true)
    } catch {
      if (mountedRef.current) setConnected(false)
    } finally {
      pollInFlight.current = false
      if (refreshQueued.current && mountedRef.current && document.visibilityState === 'visible') {
        refreshQueued.current = false
        void pollRef.current()
      }
    }
  }, [])
  pollRef.current = poll

  useEffect(() => {
    if (!solarSchedule) return
    const applySolarSchedule = () => {
      const solarState = fishingSolarStateFromSchedule(solarSchedule, new Date())
      setSolarDayPhase(solarState.dayPhase)
      setNaturePeriod(solarState.naturePeriod)
    }
    applySolarSchedule()
    const timer = window.setInterval(applySolarSchedule, 60_000)
    return () => window.clearInterval(timer)
  }, [solarSchedule])

  useEffect(() => {
    void poll()
    const timer = window.setInterval(() => void poll(), POLL_INTERVAL_MS)
    const onVisibility = () => { if (document.visibilityState === 'visible') void poll(true) }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [poll])

  useEffect(() => {
    if (activeEvent || queue.length === 0) return
    const [next, ...rest] = queue
    setQueue(rest)
    setActiveEvent(next)
  }, [activeEvent, queue])

  useEffect(() => {
    if (!activeEvent) return
    const timer = window.setTimeout(() => setActiveEvent(null), CATCH_DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [activeEvent])

  useEffect(() => {
    const onFullscreenChange = () => setFullscreen(document.fullscreenElement === rootRef.current)
    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
  }, [])

  const toggleFullscreen = async () => {
    if (!rootRef.current) return
    if (document.fullscreenElement) {
      await document.exitFullscreen()
      return
    }
    if (fullscreen) {
      setFullscreen(false)
      return
    }
    if (typeof rootRef.current.requestFullscreen === 'function') {
      try {
        await rootRef.current.requestFullscreen()
        return
      } catch {
        // iOS and embedded browsers use the CSS immersive fallback below.
      }
    }
    setFullscreen(true)
  }

  const previewEvent = (amount = 28_500, type = FishingEventType.Catch) => {
    const occurredAt = new Date().toISOString()
    const catchId = `debug-${crypto.randomUUID()}`
    setQueue((current) => [...current, {
      id: `${catchId}-${type}`,
      type,
      catchId,
      catchOccurredAt: occurredAt,
      occurredAt,
      amount,
      currency: 'RUB',
    }])
  }

  const visibleHour = debugHour ?? localHour
  const dayPhase = debugHour === null ? solarDayPhase : fishingDayPhaseAtHour(debugHour)
  const debugNaturePeriod = debugHour === null
    ? naturePeriod
    : debugHour >= 22 || debugHour < 4
      ? FishingNaturePeriod.AfterSunset
      : FishingNaturePeriod.Daylight
  const startNatureSound = useFishingNatureSound(debugNaturePeriod)

  return (
    <div
      ref={rootRef}
      className={styles.experience}
      data-fullscreen={fullscreen ? 'true' : 'false'}
      data-badger-side={visibleBadgerSide ?? undefined}
    >
      <section className={styles.sceneFrame}>
        <FishingScene
          state={activeEvent?.type === FishingEventType.Release
            ? 'release'
            : activeEvent || debugCatchFrame !== null
              ? 'catch'
              : 'idle'}
          amount={activeEvent?.amount ?? (debugCatchFrame !== null ? 40_000 : undefined)}
          catchEventId={activeEvent?.catchId}
          debugCatchFrame={debugCatchFrame}
          catchCount={todayCatchCount}
          dayPhase={dayPhase}
          worldDecision={worldDecision}
          onAmbientEventComplete={completeAmbientEvent}
          onBadgerVisitChange={setVisibleBadgerSide}
        />
        <div className={styles.fullscreenTitle}>
          <strong>Рыбалка продаж</strong>
          <span className={styles.connectionState}>
            <i className={styles.statusDot} data-connected={connected ? 'true' : 'false'} />
            {connected ? 'Маркет на связи' : 'Нет связи'}
          </span>
        </div>
        <button
          type="button"
          className={styles.sceneFullscreenButton}
          onClick={() => void toggleFullscreen()}
          aria-label={fullscreen ? 'Выйти из полноэкранного режима' : 'Открыть на весь экран'}
          title={fullscreen ? 'Свернуть' : 'На весь экран'}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
          </svg>
        </button>
        {!catchSound.audioReady && (
          <button
            type="button"
            className={styles.catchSoundUnlock}
            onClick={catchSound.enableAudio}
            aria-label="Включить звук улова"
            title="Включить звук улова"
          >
            <span aria-hidden="true">♪</span>
            Включить звук улова
          </button>
        )}
        <aside className={styles.catchLog} aria-label="Сегодняшние уловы">
          <header>
            <span>Последние уловы</span>
            <strong>×{todayCatchCount}</strong>
          </header>
          {recentEvents.length === 0 ? (
            <p>Первая рыба ещё в озере</p>
          ) : (
            <>
              <ol>
                {recentEvents.map((event) => (
                  <li key={event.id}>
                    <span className={styles.catchFish} data-tier={fishingCatchTier(event.amount)} aria-hidden="true" />
                    <strong>{formatMoney(event.amount)}</strong>
                    <time dateTime={event.occurredAt}>{formatTime(event.occurredAt)}</time>
                  </li>
                ))}
              </ol>
              <footer className={styles.catchTotal}>
                <strong>Всего: {formatMoney(todayCatchAmount)}</strong>
                {todayCatchCount > recentEvents.length && <span>Ещё {todayCatchCount - recentEvents.length} учтено</span>}
              </footer>
            </>
          )}
        </aside>
        {radioStarted && (
          <aside
            className={styles.radioDock}
            aria-label={radioProvider === FishingRadioProvider.Radio101 ? 'Мини-плеер 101.ru Lo-Fi' : 'Мини-плеер Lofi Cafe'}
            data-state={radioProvider === FishingRadioProvider.Radio101 ? (radioUnsupported ? 'unsupported' : 'playing') : undefined}
          >
            {radioProvider === FishingRadioProvider.Radio101 ? (
              radioUnsupported ? (
                <p className={styles.radioNotice} data-testid="fishing-radio-unsupported">
                  <strong>{RADIO_101_UNSUPPORTED_MESSAGE}</strong>
                  <span>{RADIO_101_UNSUPPORTED_HINT}</span>
                  <a href={RADIO_101_CHANNEL_URL} target="_blank" rel="noreferrer">{RADIO_101_UNSUPPORTED_LINK_LABEL}</a>
                </p>
              ) : (
                <audio
                  title="101.ru · Lo-Fi"
                  controls
                  autoPlay
                  preload="none"
                  onError={() => setRadioUnsupported(true)}
                >
                  <source src={RADIO_101_LOFI_STREAM_URL} type={RADIO_101_LOFI_STREAM_MIME} />
                </audio>
              )
            ) : (
              <iframe
                src={RADIO_EMBED_URL}
                title="Lofi Cafe · Working"
                allow="autoplay"
                loading="eager"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            )}
          </aside>
        )}
      {mounted && isFishingWorldDebugEnabled() && (
        <details className={styles.timeDebugger}>
          <summary>
            <i aria-hidden="true">🐞</i>
            <strong>Панель отладки</strong>
            <span>{String(visibleHour).padStart(2, '0')}:00 · {FISHING_DAY_PHASE_LABELS[dayPhase]}</span>
          </summary>
          <div className={styles.debuggerBody}>
            <section>
              <strong>Поклёвка</strong>
              <div>
                <button type="button" onClick={() => void previewEvent(5_000)}>Клёв 5к</button>
                <button type="button" onClick={() => void previewEvent(20_000)}>Клёв 20к</button>
                <button type="button" onClick={() => void previewEvent(40_000)}>Клёв 40к</button>
                <button type="button" onClick={() => void previewEvent(75_000)}>Клёв 75к</button>
                <button type="button" onClick={() => void previewEvent(150_000)}>Клёв 150к</button>
                <button type="button" onClick={() => void previewEvent(40_000, FishingEventType.Release)}>Отпустить 40к</button>
              </div>
            </section>
            <section>
              <strong>Окружение</strong>
              <div>
                {fishingWorldDebugSimulations.map((simulation) => (
                  <button type="button" key={simulation.id} onClick={() => simulateAmbientEvent(simulation.id)}>{simulation.label}</button>
                ))}
              </div>
            </section>
            <section>
              <strong>Радио по региону</strong>
              <div>
                <button
                  type="button"
                  onClick={() => {
                    startNatureSound()
                    worldAudio.current.enable()
                    setRadioProvider(FishingRadioProvider.LofiCafe)
                    setRadioUnsupported(false)
                    setRadioStarted(true)
                  }}
                >Европа · Lofi Cafe</button>
                <button
                  type="button"
                  onClick={() => {
                    startNatureSound()
                    worldAudio.current.enable()
                    setRadioProvider(FishingRadioProvider.Radio101)
                    setRadioUnsupported(false)
                    setRadioStarted(true)
                  }}
                >Россия · 101.ru</button>
                <button type="button" onClick={() => setRadioStarted(false)}>Выключить</button>
              </div>
            </section>
            <section className={styles.schedulerDiagnostics} aria-label="Диагностика планировщика">
              <strong>Диагностика планировщика</strong>
              <output data-testid="fishing-world-seed">Сид: {diagnostics.seed}</output>
              <output data-testid="fishing-world-active-events">Активные события: {activeEvents.length === 0 ? 'нет' : activeEvents.map((event) => FISHING_WORLD_EVENT_LABELS[event.kind]).join(', ')}</output>
              <output data-testid="fishing-world-next-eligibility">Следующая доступность: {Object.entries(diagnostics.nextEligibleAtMs).length === 0 ? 'нет' : Object.entries(diagnostics.nextEligibleAtMs).map(([kind, at]) => `${FISHING_WORLD_EVENT_LABELS[kind as keyof typeof FISHING_WORLD_EVENT_LABELS]} — ${formatNextEligibility(at)}`).join(', ')}</output>
              <output data-testid="fishing-world-suppression">Причина подавления: {diagnostics.suppressionReason ? SCHEDULER_SUPPRESSION_LABELS[diagnostics.suppressionReason] : 'нет'}</output>
              <output data-testid="fishing-world-debug-rejection">Отказ отладки: {diagnostics.debugRejection ? `${FISHING_WORLD_EVENT_LABELS[diagnostics.debugRejection.kind]} — ${DEBUG_REJECTION_LABELS[diagnostics.debugRejection.reason]}` : 'нет'}</output>
            </section>
            <section>
              <strong>Звук улова</strong>
              <div>
                {[0, 1, 2, 3, 4].map((variant) => (
                  <button type="button" key={variant} onClick={() => catchSound.playPreview(FishingEventType.Catch, variant as 0 | 1 | 2 | 3 | 4)}>
                    Звук {variant + 1}
                  </button>
                ))}
              </div>
            </section>
            <section>
              <strong>Звук отпускания</strong>
              <div>
                {[0, 1, 2, 3, 4].map((variant) => (
                  <button type="button" key={variant} onClick={() => catchSound.playPreview(FishingEventType.Release, variant as 0 | 1 | 2 | 3 | 4)}>
                    Отпускание {variant + 1}
                  </button>
                ))}
              </div>
            </section>
            <section className={styles.animationDebugger}>
              <strong>Анимация рыбака</strong>
              <div>
                <button type="button" onClick={() => setDebugCatchFrame(0)}>Покадрово</button>
                <button
                  type="button"
                  onClick={() => {
                    setDebugCatchFrame(null)
                    void previewEvent(40_000)
                  }}
                >▶ Целиком</button>
                <output>{debugCatchFrame === null ? 'авто' : `${debugCatchFrame + 1} / 24`}</output>
              </div>
              <input
                type="range"
                min="0"
                max="23"
                step="1"
                value={debugCatchFrame ?? 0}
                onChange={(event) => setDebugCatchFrame(Number(event.target.value))}
                aria-label="Кадр анимации рыбака"
              />
            </section>
          </div>
          <div className={styles.debuggerTime}>
            <button type="button" onClick={() => setDebugHour(null)} disabled={debugHour === null}>Авто</button>
            <input
              type="range"
              min="0"
              max="23"
              step="1"
              value={visibleHour}
              onChange={(event) => setDebugHour(Number(event.target.value))}
              aria-label="Локальный час"
            />
          </div>
        </details>
      )}
      </section>
    </div>
  )
}
